import api from "@/lib/api";
import { toast } from "sonner";

// Offline workout queue. Workouts logged while offline are stored in
// localStorage and replayed against POST /api/workouts as soon as the
// device is back online.
const KEY = "fp_offline_workouts";

const listeners = new Set();
let flushing = false;
let initialized = false;

const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
};

const write = (items) => {
  localStorage.setItem(KEY, JSON.stringify(items));
  listeners.forEach((l) => l(items.length));
};

export const getQueuedCount = () => read().length;

export const subscribeQueue = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

export function queueWorkout(payload) {
  const items = read();
  items.push({ payload, queued_at: new Date().toISOString() });
  write(items);
}

// Replays the queue. Items are only re-queued on NETWORK failure — a server
// rejection (4xx/5xx) drops the item so one bad payload can't wedge the queue.
export async function flushWorkouts() {
  if (flushing || !navigator.onLine) return 0;
  const items = read();
  if (!items.length) return 0;
  flushing = true;
  let synced = 0;
  try {
    const remaining = [];
    for (const item of items) {
      try {
        await api.post("/workouts", item.payload);
        synced += 1;
      } catch (e) {
        if (!e?.response) remaining.push(item);
      }
    }
    write(remaining);
  } finally {
    flushing = false;
  }
  if (synced) {
    toast.success(`Back online — synced ${synced} offline workout${synced > 1 ? "s" : ""}`);
  }
  return synced;
}

export function initOfflineSync() {
  if (initialized) return;
  initialized = true;
  window.addEventListener("online", () => flushWorkouts());
  flushWorkouts();
}
